import { Popover } from "@headlessui/react";
import { ArrowLongRightIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useContext } from "react";
import { ViewportContext } from "~/providers/ViewportProvider";
import { RequireOnlyOne } from "~/types/optional";

type Props = {
  name: string;
  href?: string;
  subItems?: { name: string; href: string; icon?: React.ReactNode }[];
};

export type NavItemProps = RequireOnlyOne<Props, "href" | "subItems">;

// Animacje dla dropdown
const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -10,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.2,
      ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number],
      staggerChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.95,
    transition: {
      duration: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.2 },
  },
};

export const NavItem: React.FC<NavItemProps> = ({ name, href, subItems }) => {
  const { isMobile } = useContext(ViewportContext);
  const { route } = useRouter();
  const normalizedRoute = route.replace(/\/$/, "");
  const normalizedHref = href?.replace(/\/$/, "");
  const hasActiveSubItem = subItems?.some(
    (item) => normalizedRoute === item.href.replace(/\/$/, "")
  );

  const classes = clsx(
    isMobile
      ? "px-6 py-5 w-full content-between border-gray-200 ring-inset rounded-none"
      : "px-4 py-2"
  );

  return !subItems ? (
    <Link
      href={href}
      className={clsx(
        classes,
        "text-[13px] font-medium tracking-wide transition-colors duration-300",
        normalizedHref === normalizedRoute
          ? "bg-[#E8FAF4] text-[#0C1F3F]"
          : "text-[#0C1F3F] hover:bg-[#E8FAF4] hover:text-[#0C1F3F]"
      )}
    >
      {name}
    </Link>
  ) : (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            className={clsx(
              "group flex items-center gap-1.5 text-[13px] font-medium tracking-wide transition-colors duration-300 focus:outline-none",
              classes,
              hasActiveSubItem || open
                ? "bg-[#E8FAF4] text-[#0C1F3F]"
                : "text-[#0C1F3F] hover:bg-[#E8FAF4] hover:text-[#0C1F3F]"
            )}
          >
            <span
              className={clsx(
                "transition-colors duration-300",
                hasActiveSubItem || open
                  ? "text-[#0C1F3F]"
                  : "text-[#0C1F3F] group-hover:text-[#0C1F3F]"
              )}
            >
              {name}
            </span>
            <motion.span
              animate={{ rotate: open ? 180 : 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <ChevronDownIcon
                className={clsx(
                  "h-4 w-4 transition-colors duration-300",
                  hasActiveSubItem || open
                    ? "text-[#00A87A]"
                    : "text-[#5C6370] group-hover:text-[#00A87A]"
                )}
                aria-hidden="true"
              />
            </motion.span>
          </Popover.Button>

          <AnimatePresence>
            {open && (
              <Popover.Panel
                static
                as={motion.div}
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className={clsx(
                  "absolute z-[150] mt-3 w-screen max-w-sm",
                  isMobile
                    ? "left-0 max-w-[calc(100vw-2rem)]"
                    : "left-0 -translate-x-1/4"
                )}
              >
                <div className="overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-[#E5E7EB]">
                  <div className="relative flex flex-col p-2">
                    {subItems.map((item, index) => (
                      <motion.div key={item.name} variants={itemVariants}>
                        <Link
                          href={item.href}
                          className={clsx(
                            "group/item flex items-center gap-3 px-4 py-3 text-[13px] font-medium transition-colors duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
                            index !== subItems.length - 1 &&
                              "border-b border-[#E5E7EB]",
                            normalizedRoute === item.href.replace(/\/$/, "")
                              ? "bg-[#E8FAF4] text-[#0C1F3F]"
                              : "bg-white text-[#0C1F3F] hover:bg-[#E8FAF4] hover:text-[#0C1F3F]"
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            close();
                          }}
                        >
                          <span
                            className={clsx(
                              "flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/item:scale-110",
                              normalizedRoute === item.href.replace(/\/$/, "")
                                ? "bg-white text-[#00A87A]"
                                : "bg-[#F7F8FA] text-[#5C6370] group-hover/item:bg-white group-hover/item:text-[#00A87A]"
                            )}
                          >
                            {item.icon &&
                              React.cloneElement(item.icon as React.ReactElement, {
                                className: "h-6 w-6",
                              })}
                          </span>
                          <span className="flex-1">{item.name}</span>
                          <ArrowLongRightIcon
                            className={clsx(
                              "h-5 w-5 flex-shrink-0 opacity-0 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover/item:opacity-100",
                              normalizedRoute === item.href.replace(/\/$/, "")
                                ? "text-[#00A87A] group-hover/item:text-[#00A87A]"
                                : "text-[#00A87A] group-hover/item:text-[#00A87A]"
                            )}
                          />
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </Popover.Panel>
            )}
          </AnimatePresence>
        </>
      )}
    </Popover>
  );
};
